import { useEffect, useState } from 'react'
import type { FocusEvent } from 'react'
import {
  BarChart3,
  Camera,
  CircleCheck,
  Hourglass,
  Loader2,
  Pencil,
  TriangleAlert,
} from '@/lib/icons'
import type { VideoCheckItem, VideoDetail as VideoDetailData } from '@ayub/shared'
import { calculateAmount, formatNumber, formatSom, viewsSchema } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { fetchFileBlob } from '@/components/history/api'
import { formatDateUz } from '@/lib/relative-date'
import { parseViewsInput } from './checkInput'

function getScreenshotFileId(check: VideoCheckItem): string | null {
  return check.screenshotFileId
}

/**
 * Tekshiruvlar bo'limi — `docs/03-app-flow.md` "Video kartochkasi (ochilganda)" mockupidagi
 * "📊 TEKSHIRUVLAR" bloki. Bosqich 7/8'dagi versiya faqat O'QISH uchun edi (docs izohiga
 * qarang) — bu Bosqich 9: kelayotgan tekshiruv endi kiritish maydoniga aylanadi va yakunlangan
 * tekshiruvlar tuzatish (✏️) tugmasi oladi. Mutatsiyalarning o'zi (`useMutation`) ATAYLAB shu
 * yerda EMAS, `VideoDetail.tsx`da — bu fayl faqat taqdimot va mahalliy forma holati, xuddi
 * shu papkadagi `InstagramSection`/`DecisionFooter` naqshi bilan bir xil.
 */

// ─────────────────────────────── Umumiy qismlar ───────────────────────────────

interface ViewsInputProps {
  id: string
  value: string
  onChange: (value: string) => void
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void
  disabled?: boolean
  autoFocus?: boolean
}

/**
 * `type="text"` + `inputMode="numeric"` ATAYLAB shu kombinatsiyada: `inputMode="numeric"`
 * telefonda raqamli klaviaturani chiqaradi, lekin `type="number"`dan farqli, brauzer
 * bo'shliq/vergul kabi belgilarni kiritishga TO'SQINLIK QILMAYDI. Vazifa talabi — admin
 * blogerning skrinshotidan yoki Instagram'ning o'zidan "12 400" yoki "12,400" ko'rinishidagi
 * qiymatni to'g'ridan-to'g'ri joylashtira (paste) olishi kerak; `type="number"` bunday
 * qiymatni yoki butunlay rad etadi, yoki jimgina bo'shatib qo'yadi. Tozalash shu o'rniga
 * `parseViewsInput()`ga (`checkInput.ts`) yuklangan.
 *
 * `text-base` (16px) ATAYLAB — bundan kichik shriftda iOS Safari inputga fokuslanganda
 * butun sahifani avtomatik kattalashtiradi, natijada pastdagi Saqlash tugmasi ekrandan
 * chiqib ketishi mumkin (klassik mobil forma xatosi, vazifa talabida alohida ta'kidlangan).
 */
function ViewsInput({ id, value, onChange, onFocus, disabled, autoFocus }: ViewsInputProps) {
  return (
    <input
      id={id}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      autoFocus={autoFocus}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onFocus={onFocus}
      disabled={disabled}
      placeholder="Masalan: 12 400"
      className="tabular h-11 w-full rounded-input border border-app-border bg-app-bg px-3 text-base text-app-text outline-none focus-visible:border-brand disabled:opacity-60"
    />
  )
}

/**
 * "Jonli hisob" — `docs/03-app-flow.md`: "Prosmotr kiritilganda summa DARHOL, saqlashdan
 * oldin hisoblanadi". `calculateAmount()` (`@ayub/shared`) BUNDAN BOSHQA formula bilan
 * QAYTA YOZILMAYDI: aynan shu funksiyaning o'zi butun loyihadagi pul hisobining yagona
 * manbasi (`format.ts`dagi izoh) — ko'paytirish bo'lishdan OLDIN bajariladi, aks holda
 * 43 prosmotr × 5000 so'mda noto'g'ri 214 (to'g'risi 215) kabi xatolar qaytadi. Agar bu
 * ekran mustaqil `views / 1000 * rate` yozsa, jonli ko'rsatkich va server saqlagan summa
 * ATAMA-ATAMA kelishmasligi mumkin — bu butun ekranning ishonch maqsadini buzadi.
 */
function AmountPreview({ views, ratePer1000 }: { views: number | null; ratePer1000: number }) {
  const amount = views !== null ? calculateAmount(views, ratePer1000) : null
  return (
    <div className="flex items-center justify-between rounded-button bg-brand-subtle px-3 py-2">
      <span className="text-xs font-medium text-app-hint">Summa (jonli hisob)</span>
      <span className="tabular text-base font-semibold text-brand">
        {amount !== null ? formatSom(amount) : '—'}
      </span>
    </div>
  )
}

/** Kiritilgan matnni tekshirib, yaroqli (chegaradan oshmagan, manfiy bo'lmagan) qiymatga aylantiradi. */
function useValidatedViews(raw: string) {
  const parsed = parseViewsInput(raw)
  const bounds = parsed !== null ? viewsSchema.safeParse(parsed) : null
  const valid = bounds?.success ? parsed : null
  const formatIssue = raw.trim().length > 0 && parsed === null
  const boundsIssue = bounds !== null && !bounds.success ? bounds.error.issues[0]?.message : null
  return { valid, formatIssue, boundsIssue }
}

// ─────────────────────────────── Yakunlangan qator ───────────────────────────────

function CheckRow({
  check,
  onEdit,
  onViewScreenshot,
}: {
  check: VideoCheckItem
  onEdit: () => void
  onViewScreenshot: (fileId: string) => void
}) {
  const screenshotFileId = getScreenshotFileId(check)

  return (
    <li className="flex items-center justify-between gap-3 py-2 text-sm">
      <span className="inline-flex items-center gap-2">
        <CircleCheck size={14} className="text-urgency-done-text" aria-hidden="true" />
        Tekshiruv ({formatDateUz(check.checkedAt)})
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="tabular font-medium text-app-text">
          {formatNumber(check.views)} · {formatSom(check.amount)}
        </span>
        {screenshotFileId && (
          <button
            type="button"
            onClick={() => onViewScreenshot(screenshotFileId)}
            aria-label="Tekshiruv skrinshotini ko'rish"
            title="Skrinshot"
            className="flex size-11 shrink-0 items-center justify-center rounded-button text-app-hint transition-colors duration-150 ease-out hover:bg-app-bg hover:text-brand"
          >
            <Camera size={14} aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          onClick={onEdit}
          aria-label="Tekshiruvni tuzatish"
          title="Tuzatish"
          className="flex size-11 shrink-0 items-center justify-center rounded-button text-app-hint transition-colors duration-150 ease-out hover:bg-app-bg hover:text-brand"
        >
          <Pencil size={14} aria-hidden="true" />
        </button>
      </span>
    </li>
  )
}

// ─────────────────────────────── Kutilayotgan tekshiruv (kiritish) ───────────────────────────────

interface PendingCheckFormProps {
  ratePer1000: number
  isSubmitting: boolean
  errorMessage: string | null
  onSubmit: (views: number) => void
}

/**
 * Video uchun YAGONA tekshiruv kiritish maydoni — saqlansa hisob darhol yakunlanadi.
 * Yakuniy tasdiq `VideoDetail.tsx`dagi `ConfirmDialog` orqali so'raladi.
 */
function PendingCheckForm({
  ratePer1000,
  isSubmitting,
  errorMessage,
  onSubmit,
}: PendingCheckFormProps) {
  const [rawViews, setRawViews] = useState('')
  const { valid, formatIssue, boundsIssue } = useValidatedViews(rawViews)
  const inputId = 'check-views'
  // "Qiymat o'zgarmagan" holati bu yerda "hali hech narsa kiritilmagan" bilan bir xil —
  // yangi tekshiruvda solishtiriladigan oldingi qiymat yo'q, shuning uchun bo'sh/yaroqsiz
  // kirish tabiiy ravishda tugmani o'chirib turadi (vazifa talabidagi ikkinchi shart bilan
  // ustma-ust tushadi: `EditCheckModal`da esa haqiqiy "eski qiymat" bilan solishtiriladi).
  const canSubmit = valid !== null && !isSubmitting

  /**
   * Mobil klaviatura ochilganda maydon (va pastdagi Saqlash tugmasi) ko'rinib turishi
   * kerak (vazifa talabi — "input hidden behind keyboard" klassik xatosi). Fokusdan darhol
   * keyin emas, kichik kechikish bilan sudraladi: klaviatura animatsiyasi tugagunча
   * `scrollIntoView` chaqirilsa, hisoblangan pozitsiya hali klaviatura balandligini
   * hisobga olmagan bo'ladi.
   */
  function handleFocus(event: FocusEvent<HTMLInputElement>) {
    const el = event.currentTarget
    window.setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)
  }

  function handleSubmit() {
    if (valid === null) return
    onSubmit(valid)
  }

  return (
    <li className="flex flex-col gap-2 py-2">
      <span className="inline-flex items-center gap-2 text-sm font-medium text-app-text">
        <Hourglass size={14} className="text-app-hint" aria-hidden="true" />
        Tekshiruv · Yakuniy
      </span>

      <label htmlFor={inputId} className="text-xs font-medium text-app-hint">
        Prosmotrlar soni
      </label>
      <ViewsInput
        id={inputId}
        value={rawViews}
        onChange={setRawViews}
        onFocus={handleFocus}
        disabled={isSubmitting}
      />
      {formatIssue && (
        <p className="text-xs text-stage-rejected-text">Faqat raqam kiriting (masalan: 12 400)</p>
      )}
      {boundsIssue && <p className="text-xs text-stage-rejected-text">{boundsIssue}</p>}

      <AmountPreview views={valid} ratePer1000={ratePer1000} />

      {errorMessage && (
        <p className="text-xs font-medium text-stage-rejected-text">{errorMessage}</p>
      )}

      <Button size="sm" className="self-start" onClick={handleSubmit} disabled={!canSubmit}>
        {isSubmitting && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
        Saqlash
      </Button>
    </li>
  )
}

// ─────────────────────────────── Bo'lim (export) ───────────────────────────────

export interface ChecksSectionProps {
  video: VideoDetailData
  onSubmitCheck: (views: number) => void
  isSubmitting: boolean
  submitError: string | null
  onRequestEdit: (check: VideoCheckItem) => void
  onViewScreenshot: (fileId: string) => void
}

export function ChecksSection({
  video,
  onSubmitCheck,
  isSubmitting,
  submitError,
  onRequestEdit,
  onViewScreenshot,
}: ChecksSectionProps) {
  const hasAnyCheck = video.checks.length > 0 || video.checkPending
  // `ratePer1000` `POSTED`ga o'tganda muzlatiladi (docs/05) — `CHECKING` bosqichida har doim
  // to'ldirilgan bo'lishi kerak. Tip `number | null` (server hali qurilmagan bosqichlar uchun
  // ham qamrab oladi), shuning uchun `?? 0` — `calculateAmount()` 0 stavkada 0 qaytaradi,
  // demak jonli hisob hech qachon yiqilmaydi, faqat "—" o'rniga "0 so'm" ko'rsatilib qoladi.
  const rate = video.ratePer1000 ?? 0

  return (
    <section className="flex flex-col gap-2 rounded-card border border-app-border bg-app-card p-4">
      <h2 className="inline-flex items-center gap-2 text-sm font-semibold">
        <BarChart3 size={16} className="text-brand" aria-hidden="true" />
        Tekshiruvlar
      </h2>
      {!hasAnyCheck ? (
        <p className="text-sm text-app-hint">Hali tekshiruv boshlanmagan.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-app-border">
          {video.checks.map((check) => (
            <CheckRow
              key={check.checkedAt}
              check={check}
              onEdit={() => onRequestEdit(check)}
              onViewScreenshot={onViewScreenshot}
            />
          ))}
          {video.checkPending && (
            <PendingCheckForm
              ratePer1000={rate}
              isSubmitting={isSubmitting}
              errorMessage={submitError}
              onSubmit={onSubmitCheck}
            />
          )}
        </ul>
      )}
    </section>
  )
}

// ─────────────────────────────── Tuzatish oynasi (export) ───────────────────────────────

export interface EditCheckModalProps {
  /** `null` — oyna yopiq. */
  check: VideoCheckItem | null
  ratePer1000: number
  isSubmitting: boolean
  errorMessage: string | null
  onCancel: () => void
  onSubmit: (views: number, note: string | undefined) => void
}

/**
 * `docs/06-implementation-plan.md` Bosqich 9: "Tuzatish imkoniyati (PATCH) — ActivityLog'ga
 * eski qiymat bilan". Video uchun yagona tekshiruv hisobni yopgani uchun uni tuzatish
 * blogerga tegishli to'lov miqdorini o'zgartiradi — shuning uchun ALOHIDA tasdiqlanishi
 * kerak: bu oyna o'zi FAQAT yangi qiymat/izohni yig'adi, tugma "Davom etish" deb ataladi va
 * `VideoDetail.tsx`dagi ikkinchi bosqich (`ConfirmDialog`) so'ngi tasdiqni so'raydi.
 */
export function EditCheckModal({
  check,
  ratePer1000,
  isSubmitting,
  errorMessage,
  onCancel,
  onSubmit,
}: EditCheckModalProps) {
  const [rawViews, setRawViews] = useState('')
  const [note, setNote] = useState('')

  // Har safar BOSHQA tekshiruv tuzatish uchun ochilganda — oldingi forma holati (masalan
  // avvalgi izoh) qolib ketmasin. Boshlang'ich qiymat sifatida joriy prosmotr ko'rsatiladi
  // (formatlangan holda — `parseViewsInput()` NBSP/bo'shliqni baribir to'g'ri o'qiydi).
  useEffect(() => {
    if (check) {
      setRawViews(formatNumber(check.views))
      setNote('')
    }
  }, [check])

  const { valid, formatIssue } = useValidatedViews(rawViews)
  const isUnchanged = check !== null && valid === check.views
  const canSubmit = valid !== null && !isUnchanged && !isSubmitting

  function handleSubmit() {
    if (valid === null || isUnchanged) return
    onSubmit(valid, note.trim().length > 0 ? note.trim() : undefined)
  }

  return (
    <Modal
      open={check !== null}
      onClose={onCancel}
      ariaLabel="Tekshiruvni tuzatish"
      size="compact"
      zIndex={50}
    >
      <div className="flex flex-col gap-4 p-5">
        <h2 className="text-base font-semibold">Tekshiruvni tuzatish (yakuniy)</h2>

        {check !== null && (
          <p className="inline-flex items-start gap-1.5 rounded-button bg-urgency-today/10 px-2.5 py-2 text-xs font-medium text-urgency-today-text">
            <TriangleAlert size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
            Bu YAKUNIY tekshiruv — natija allaqachon blogerning balansiga qo&apos;shilgan.
            Tasdiqlash keyingi qadamda so&apos;raladi.
          </p>
        )}

        <div className="flex flex-col gap-1">
          <label htmlFor="edit-check-views" className="text-xs font-medium text-app-hint">
            Prosmotrlar soni
          </label>
          <ViewsInput
            id="edit-check-views"
            value={rawViews}
            onChange={setRawViews}
            disabled={isSubmitting}
            autoFocus
          />
          {formatIssue && (
            <p className="text-xs text-stage-rejected-text">
              Faqat raqam kiriting (masalan: 12 400)
            </p>
          )}
        </div>

        <AmountPreview views={valid} ratePer1000={ratePer1000} />

        <div className="flex flex-col gap-1">
          <label htmlFor="edit-check-note" className="text-xs font-medium text-app-hint">
            Tuzatish sababi (ixtiyoriy)
          </label>
          <textarea
            id="edit-check-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            placeholder="Masalan: birinchi marta noto'g'ri raqam kiritilgan edi"
            className="rounded-input border border-app-border bg-app-bg px-3 py-2 text-base text-app-text outline-none focus-visible:border-brand"
          />
        </div>

        {errorMessage && (
          <p className="text-xs font-medium text-stage-rejected-text">{errorMessage}</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Bekor qilish
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            Davom etish
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// ─────────────────────────────── Skrinshot ko'rish oynasi (export) ───────────────────────────────

interface ScreenshotBlobState {
  url: string | null
  loading: boolean
  failed: boolean
}

/**
 * `GET /api/files/:fileId` bayt oqimini `URL.createObjectURL()` orqali `<img>`ga
 * bog'laydigan holat — `fileId` o'zgarganda (yoki `retry` bosilganda) qayta yuklanadi,
 * effekt tozalanganda (`fileId` o'zgarishi, `retry`, yoki modal yopilishi — komponent
 * unmount bo'lishi) ESKI blob URL DARHOL `revokeObjectURL()` bilan bo'shatiladi — aks
 * holda har ochilgan skrinshot xotirada qolib ketardi (vazifa talabida alohida
 * ta'kidlangan xotira sizishi xavfi).
 */
function useScreenshotBlob(fileId: string | null): ScreenshotBlobState & { retry: () => void } {
  const [state, setState] = useState<ScreenshotBlobState>({
    url: null,
    loading: false,
    failed: false,
  })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (fileId === null) {
      setState({ url: null, loading: false, failed: false })
      return
    }

    let cancelled = false
    let objectUrl: string | null = null
    setState({ url: null, loading: true, failed: false })

    fetchFileBlob(fileId)
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setState({ url: objectUrl, loading: false, failed: false })
      })
      .catch(() => {
        if (!cancelled) setState({ url: null, loading: false, failed: true })
      })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
    // `attempt` — faqat qayta urinishni MAJBURLASH uchun dependency (qiymatning o'zi
    // ishlatilmaydi), "Qayta urinish" bosilganda effekt qayta ishga tushishi kerak.
  }, [fileId, attempt])

  return { ...state, retry: () => setAttempt((a) => a + 1) }
}

export interface ScreenshotModalProps {
  /** `null` — oyna yopiq. */
  fileId: string | null
  onClose: () => void
}

/**
 * Vazifa 2 (skrinshot ko'rish) — `VideoCheckItem.screenshotFileId` bo'lgan qatordagi
 * 📷 tugma shu oynani ochadi. Rasm to'g'ridan-to'g'ri `<img src="/api/files/:id">` bilan
 * KO'RSATILMAYDI (fayl endpointi autentifikatsiya talab qiladi, oddiy `<img>` so'rovi
 * `Authorization` sarlavhasini yubora olmaydi) — buning o'rniga `fetchFileBlob()`
 * (`components/history/api.ts`) autentifikatsiyalangan `fetch()` bilan baytlarni oladi,
 * `URL.createObjectURL()` esa natijani `<img>` ko'rsata oladigan vaqtinchalik havolaga
 * aylantiradi.
 */
export function ScreenshotModal({ fileId, onClose }: ScreenshotModalProps) {
  const { url, loading, failed, retry } = useScreenshotBlob(fileId)

  return (
    <Modal
      open={fileId !== null}
      onClose={onClose}
      ariaLabel="Tekshiruv skrinshoti"
      size="compact"
      zIndex={50}
    >
      <div className="flex flex-col gap-3 p-5">
        <h2 className="text-base font-semibold">Skrinshot</h2>

        {loading && (
          <div className="flex min-h-[240px] items-center justify-center">
            <Loader2 size={24} className="animate-spin text-app-hint" aria-hidden="true" />
          </div>
        )}

        {!loading && failed && (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <TriangleAlert size={20} className="text-app-hint" aria-hidden="true" />
            <p className="text-sm text-app-hint">Yuklab bo&apos;lmadi</p>
            <Button size="sm" variant="secondary" onClick={retry}>
              Qayta urinish
            </Button>
          </div>
        )}

        {!loading && !failed && url && (
          <img
            src={url}
            alt="Tekshiruv skrinshoti"
            className="max-h-[70vh] w-full rounded-button object-contain"
          />
        )}

        <Button variant="secondary" className="self-end" onClick={onClose}>
          Yopish
        </Button>
      </div>
    </Modal>
  )
}
